/**
 * HostessGuestCounterPage — publiczna strona dla hostess.
 * Bez logowania. URL: /hostess/:location
 *
 * Flow:
 *   1) Gate "Wpisz swoje dane" — dwie hostessy muszą wpisać imię i nazwisko + zaznaczyć
 *      checkbox potwierdzenia że dane są prawdziwe.
 *   2) Po zatwierdzeniu — ekran liczenia gości (przyciski 2-8 + ręczny + Zakończ pracę).
 *   3) Imiona zapamiętane w localStorage żeby nie wpisywać po odświeżeniu.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LOCATIONS = {
  krakow: { label: 'Kraków', color: '#d4af37', icon: '🏛️' },
  nowysacz: { label: 'Nowy Sącz', color: '#10b981', icon: '🏪' },
  grodek: { label: 'Gródek nad Dunajcem', color: '#3b82f6', icon: '🏞️' },
};

const QUICK_BUTTONS = [2, 3, 4, 5, 6, 7, 8];

const HostessGuestCounterPage = () => {
  const { location } = useParams();
  const loc = LOCATIONS[location];
  const storageKey = `hostess-shift:${location}`;

  const [shift, setShift] = useState(null); // {hostess1, hostess2, startedAt}
  const [recent, setRecent] = useState([]);
  const [dailyTotal, setDailyTotal] = useState(0);
  const [dailyEntries, setDailyEntries] = useState(0);
  const [busy, setBusy] = useState(false);
  const [customCount, setCustomCount] = useState('');
  const [toastMsg, setToastMsg] = useState(null);

  // Wczytaj zmianę z localStorage
  useEffect(() => {
    if (!loc) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) setShift(JSON.parse(raw));
    } catch { /* ignore */ }
  }, [loc, storageKey]);

  const loadRecent = useCallback(async () => {
    if (!loc) return;
    try {
      const date = new Date().toISOString().slice(0, 10);
      const r = await axios.get(`${API}/api/guest-counts`, { params: { location, date } });
      const items = r.data?.items || [];
      setRecent(items.slice(0, 8));
      setDailyTotal(Number(r.data?.total || 0));
      setDailyEntries(items.filter((x) => x.type !== 'shift_end').length);
    } catch (e) { /* ignore */ }
  }, [location, loc]);

  useEffect(() => { if (shift) loadRecent(); }, [loadRecent, shift]);

  const showToast = (msg, ms = 2500) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), ms);
  };

  const submitCount = async (count) => {
    if (!shift) return;
    if (!count || count < 1) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/guest-counts`, {
        location,
        count,
        type: 'count',
        hostessNames: [shift.hostess1, shift.hostess2].filter(Boolean),
      });
      showToast(`✅ Dodano ${count} gości!`);
      setCustomCount('');
      loadRecent();
      if (navigator.vibrate) navigator.vibrate(40);
    } catch (e) {
      showToast('❌ Błąd zapisu — spróbuj ponownie', 3000);
    } finally {
      setBusy(false);
    }
  };

  const submitAdjustment = async (count, reason) => {
    if (!shift) return;
    if (!count || count < 1) return;
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/guest-counts`, {
        location,
        count,
        type: 'adjustment',
        note: reason.trim(),
        hostessNames: [shift.hostess1, shift.hostess2].filter(Boolean),
      });
      showToast(`✅ Odjęto ${count} gości`);
      loadRecent();
      if (navigator.vibrate) navigator.vibrate([40, 50, 40]);
    } catch (e) {
      showToast('❌ Błąd zapisu — spróbuj ponownie', 3000);
    } finally {
      setBusy(false);
    }
  };

  const submitNote = async (text) => {
    if (!shift) return;
    if (!text || !text.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/guest-counts`, {
        location,
        count: 0,
        type: 'note',
        note: text.trim(),
        hostessNames: [shift.hostess1, shift.hostess2].filter(Boolean),
      });
      showToast('📝 Notatka wysłana do managera');
      loadRecent();
    } catch (e) {
      showToast('❌ Błąd zapisu — spróbuj ponownie', 3000);
    } finally {
      setBusy(false);
    }
  };

  const endShift = async () => {
    if (!shift) return;
    if (!await window.confirm('Zakończyć dzisiejszą zmianę? Manager zobaczy godzinę zakończenia.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/guest-counts`, {
        location,
        count: 0,
        type: 'shift_end',
        hostessNames: [shift.hostess1, shift.hostess2].filter(Boolean),
        note: 'Koniec zmiany hostess',
      });
      localStorage.removeItem(storageKey);
      setShift(null);
      showToast('👋 Zmiana zakończona. Do zobaczenia!', 3500);
    } catch (e) {
      showToast('❌ Nie udało się zapisać zakończenia', 3000);
    } finally {
      setBusy(false);
    }
  };

  const startShift = (h1, h2) => {
    const payload = {
      hostess1: h1.trim(),
      hostess2: h2.trim(),
      startedAt: new Date().toISOString(),
    };
    localStorage.setItem(storageKey, JSON.stringify(payload));
    setShift(payload);
  };

  if (!loc) {
    return (
      <div style={pageStyle('#1a1a1a', '#fff')}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 64, marginBottom: 10 }}>❌</div>
          <h1>Nieprawidłowy link</h1>
          <p style={{ opacity: 0.7 }}>Lokalizacja „{location}" nie istnieje.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle(loc.color, '#fff')} data-testid="hostess-page">
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '20px 16px 40px' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 38, marginBottom: 4 }}>{loc.icon}</div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800 }}>MR JERRY</h1>
          <div style={{ opacity: 0.85, fontSize: 16, marginTop: 4 }}>
            Hostessa — {loc.label}
          </div>
          <div style={{ opacity: 0.6, fontSize: 12, marginTop: 6 }}>
            {new Date().toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>

        {!shift ? (
          <ShiftStart onStart={startShift} />
        ) : (
          <CounterScreen
            shift={shift}
            recent={recent}
            dailyTotal={dailyTotal}
            dailyEntries={dailyEntries}
            busy={busy}
            customCount={customCount}
            setCustomCount={setCustomCount}
            submitCount={submitCount}
            submitAdjustment={submitAdjustment}
            submitNote={submitNote}
            endShift={endShift}
          />
        )}

        <div style={{ marginTop: 30, textAlign: 'center', fontSize: 11, opacity: 0.45 }}>
          Anonimowy licznik gości • dane widoczne tylko dla managera
        </div>
      </div>

      {toastMsg && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#22c55e', color: '#fff',
          padding: '14px 24px', borderRadius: 999,
          fontWeight: 800, fontSize: 15,
          boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
          zIndex: 9999,
          animation: 'hostessToastIn 0.3s ease',
        }}>
          {toastMsg}
        </div>
      )}

      <style>{`
        @keyframes hostessToastIn {
          from { opacity: 0; transform: translate(-50%, 20px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button {
          -webkit-appearance: none; margin: 0;
        }
      `}</style>
    </div>
  );
};

// =====================================================================
// Gate przy rozpoczęciu zmiany — imiona obu hostess + zgoda
// =====================================================================
const ShiftStart = ({ onStart }) => {
  const [h1, setH1] = useState('');
  const [h2, setH2] = useState('');
  const [agreed, setAgreed] = useState(false);

  const valid = h1.trim().length >= 3 && h2.trim().length >= 3 && agreed;

  return (
    <div data-testid="hostess-shift-start">
      <div style={{
        background: 'rgba(239, 68, 68, 0.12)',
        border: '1px solid rgba(239, 68, 68, 0.45)',
        borderLeft: '4px solid #ef4444',
        borderRadius: 14,
        padding: '14px 16px',
        marginBottom: 18,
      }}>
        <div style={{
          fontSize: 12, fontWeight: 800, letterSpacing: 0.6,
          color: '#fca5a5', textTransform: 'uppercase', marginBottom: 4,
        }}>
          ⚠️ Uwaga — dane są weryfikowane
        </div>
        <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>
          Wprowadzaj <b>wyłącznie prawdziwe dane</b>. Każda liczba gości jest logowana wraz z czasem oraz danymi zmiany. <b>Za zaniżanie, zawyżanie lub jakąkolwiek manipulację liczbą gości będą wyciągane konsekwencje służbowe.</b>
        </div>
      </div>

      <h2 style={{ fontSize: 18, margin: '0 0 12px' }}>👥 Rozpocznij zmianę</h2>
      <p style={{ opacity: 0.8, fontSize: 13.5, marginBottom: 16, marginTop: 0 }}>
        Wpiszcie obie hostessy które rozpoczynają zmianę. Bez wypełnienia obu pól nie da się dodawać gości.
      </p>

      <NameInput
        label="👤 Hostessa #1 — imię i nazwisko"
        value={h1}
        onChange={setH1}
        testId="hostess-name-1"
        placeholder="np. Anna Kowalska"
      />
      <NameInput
        label="👤 Hostessa #2 — imię i nazwisko"
        value={h2}
        onChange={setH2}
        testId="hostess-name-2"
        placeholder="np. Joanna Nowak"
      />

      <label
        data-testid="hostess-confirm-agree"
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 14px', marginTop: 8, marginBottom: 18,
          background: agreed ? 'rgba(34, 197, 94, 0.12)' : 'rgba(255,255,255,0.06)',
          border: `1px solid ${agreed ? 'rgba(34, 197, 94, 0.4)' : 'rgba(255,255,255,0.15)'}`,
          borderRadius: 12,
          cursor: 'pointer',
          fontSize: 13.5, lineHeight: 1.4,
        }}
      >
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          style={{
            marginTop: 2, width: 20, height: 20,
            accentColor: '#22c55e', cursor: 'pointer', flexShrink: 0,
          }}
        />
        <span>
          Potwierdzam, że <b>liczba gości</b> wprowadzana podczas tej zmiany będzie <b>zgodna z rzeczywistością</b>.
        </span>
      </label>

      <button
        onClick={() => valid && onStart(h1, h2)}
        disabled={!valid}
        data-testid="hostess-start-btn"
        style={{
          width: '100%',
          padding: '16px 20px',
          fontSize: 17,
          fontWeight: 900,
          borderRadius: 14,
          border: 'none',
          background: valid ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'rgba(255,255,255,0.15)',
          color: valid ? '#fff' : 'rgba(255,255,255,0.5)',
          cursor: valid ? 'pointer' : 'not-allowed',
          boxShadow: valid ? '0 8px 22px rgba(34,197,94,0.4)' : 'none',
          letterSpacing: 0.3,
        }}
      >
        🚀 Rozpocznij zmianę
      </button>
    </div>
  );
};

const NameInput = ({ label, value, onChange, placeholder, testId }) => (
  <label style={{ display: 'block', marginBottom: 12 }}>
    <div style={{ fontSize: 12.5, fontWeight: 700, opacity: 0.85, marginBottom: 5 }}>{label}</div>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      autoCapitalize="words"
      style={{
        width: '100%',
        boxSizing: 'border-box',
        padding: '14px 16px',
        fontSize: 16,
        fontWeight: 600,
        borderRadius: 12,
        border: '1px solid rgba(255,255,255,0.2)',
        background: 'rgba(255,255,255,0.92)',
        color: '#1a1a1a',
        outline: 'none',
      }}
    />
  </label>
);

// =====================================================================
// Ekran liczenia gości
// =====================================================================
const CounterScreen = ({ shift, recent, dailyTotal, dailyEntries, busy, customCount, setCustomCount, submitCount, submitAdjustment, submitNote, endShift }) => {
  const startedTime = shift?.startedAt ? new Date(shift.startedAt).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }) : '';
  const [showSubtract, setShowSubtract] = useState(false);
  const [showNote, setShowNote] = useState(false);

  return (
    <>
      {/* Header zmiany */}
      <div style={{
        background: 'rgba(34, 197, 94, 0.12)',
        border: '1px solid rgba(34, 197, 94, 0.35)',
        borderRadius: 14,
        padding: '11px 14px',
        marginBottom: 16,
        fontSize: 13,
      }}>
        <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 2 }}>
          🟢 Zmiana trwa od {startedTime}
        </div>
        <div style={{ opacity: 0.9, fontSize: 13 }}>
          {shift.hostess1} <span style={{ opacity: 0.5 }}>+</span> {shift.hostess2}
        </div>
      </div>

      {/* Licznik dziś */}
      <div style={{
        background: 'rgba(255,255,255,0.08)',
        backdropFilter: 'blur(10px)',
        borderRadius: 20,
        padding: '18px 20px',
        marginBottom: 18,
        border: '1px solid rgba(255,255,255,0.15)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 11, opacity: 0.75, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700 }}>
          Dziś przyjęto
        </div>
        <div style={{ fontSize: 56, fontWeight: 900, lineHeight: 1, marginTop: 6 }} data-testid="hostess-daily-total">
          {dailyTotal}
        </div>
        <div style={{ fontSize: 13, opacity: 0.7, marginTop: 4 }}>
          gości ({dailyEntries} wpisów)
        </div>
      </div>

      <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 8, fontWeight: 700, letterSpacing: 0.3 }}>
        Dodaj gości — szybkie przyciski:
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 10,
        marginBottom: 18,
      }}>
        {QUICK_BUTTONS.map((n) => (
          <button
            key={n}
            onClick={() => submitCount(n)}
            disabled={busy}
            data-testid={`hostess-btn-plus-${n}`}
            style={{
              padding: '20px 10px',
              fontSize: 26,
              fontWeight: 900,
              border: 'none',
              borderRadius: 16,
              background: 'rgba(255,255,255,0.92)',
              color: '#1a1a1a',
              cursor: busy ? 'wait' : 'pointer',
              boxShadow: '0 6px 18px rgba(0,0,0,0.32), inset 0 -3px 0 rgba(0,0,0,0.12)',
              transition: 'transform 0.1s',
              opacity: busy ? 0.7 : 1,
              lineHeight: 1,
            }}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.96)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            +{n}
            <div style={{ fontSize: 11, fontWeight: 700, color: '#666', marginTop: 4 }}>gości</div>
          </button>
        ))}
      </div>

      <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 8, fontWeight: 700, letterSpacing: 0.3 }}>
        Lub wpisz ręcznie:
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); submitCount(parseInt(customCount, 10)); }}
        style={{ display: 'flex', gap: 8, marginBottom: 18 }}
      >
        <input
          type="number"
          min="1"
          max="999"
          value={customCount}
          onChange={(e) => setCustomCount(e.target.value)}
          placeholder="np. 12"
          data-testid="hostess-custom-input"
          inputMode="numeric"
          style={{
            flex: 1, padding: '14px 18px', fontSize: 20, fontWeight: 700,
            borderRadius: 14, border: 'none',
            background: 'rgba(255,255,255,0.92)', color: '#1a1a1a',
            outline: 'none', textAlign: 'center',
          }}
        />
        <button
          type="submit"
          disabled={busy || !customCount}
          data-testid="hostess-custom-submit"
          style={{
            padding: '14px 24px', fontSize: 18, fontWeight: 800,
            borderRadius: 14, border: 'none',
            background: !customCount ? 'rgba(255,255,255,0.2)' : '#22c55e',
            color: '#fff',
            cursor: !customCount || busy ? 'not-allowed' : 'pointer',
            boxShadow: !customCount ? 'none' : '0 6px 16px rgba(34,197,94,0.4)',
          }}
        >✓</button>
      </form>

      {/* Akcje dodatkowe — odejmij / notatka */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <button
          onClick={() => setShowSubtract(true)}
          disabled={busy}
          data-testid="hostess-subtract-btn"
          style={{
            padding: '13px 12px',
            fontSize: 14, fontWeight: 800,
            borderRadius: 12,
            border: '1px solid rgba(251, 146, 60, 0.45)',
            background: 'rgba(251, 146, 60, 0.14)',
            color: '#fdba74',
            cursor: busy ? 'wait' : 'pointer',
            letterSpacing: 0.2,
          }}
        >
          ➖ Odejmij gości
        </button>
        <button
          onClick={() => setShowNote(true)}
          disabled={busy}
          data-testid="hostess-note-btn"
          style={{
            padding: '13px 12px',
            fontSize: 14, fontWeight: 800,
            borderRadius: 12,
            border: '1px solid rgba(59, 130, 246, 0.45)',
            background: 'rgba(59, 130, 246, 0.14)',
            color: '#93c5fd',
            cursor: busy ? 'wait' : 'pointer',
            letterSpacing: 0.2,
          }}
        >
          📝 Wyślij notatkę
        </button>
      </div>

      {/* Zakończ pracę */}
      <button
        onClick={endShift}
        disabled={busy}
        data-testid="hostess-end-shift-btn"
        style={{
          width: '100%',
          padding: '14px 16px',
          fontSize: 14, fontWeight: 800,
          borderRadius: 12,
          border: '1px solid rgba(239, 68, 68, 0.4)',
          background: 'rgba(239, 68, 68, 0.12)',
          color: '#fca5a5',
          cursor: busy ? 'wait' : 'pointer',
          marginBottom: 18,
          letterSpacing: 0.3,
        }}
      >
        👋 Zakończ pracę
      </button>

      {recent.length > 0 && (
        <div style={{
          background: 'rgba(0,0,0,0.25)',
          borderRadius: 14,
          padding: '12px 14px',
          border: '1px solid rgba(255,255,255,0.1)',
        }}>
          <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5, marginBottom: 8 }}>
            Ostatnie wpisy z dziś
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {recent.slice(0, 6).map((r) => {
              const isEnd = r.type === 'shift_end';
              const isAdj = r.type === 'adjustment';
              const isNote = r.type === 'note';
              const bg = isEnd ? 'rgba(239, 68, 68, 0.15)'
                : isAdj ? 'rgba(251, 146, 60, 0.15)'
                : isNote ? 'rgba(59, 130, 246, 0.15)'
                : 'rgba(255,255,255,0.06)';
              const chipBg = isEnd ? '#ef4444' : isAdj ? '#fb923c' : isNote ? '#3b82f6' : '#22c55e';
              const chipText = isEnd ? '👋' : isAdj ? `−${r.count}` : isNote ? '📝' : `+${r.count}`;
              return (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '8px 10px',
                  background: bg,
                  borderRadius: 10, fontSize: 14,
                }}>
                  <div style={{
                    minWidth: 44, padding: '4px 8px', borderRadius: 8,
                    background: chipBg, color: '#fff',
                    fontWeight: 800, fontSize: isEnd || isNote ? 13 : 15, textAlign: 'center',
                  }}>
                    {chipText}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ opacity: 0.85, fontSize: 13 }}>
                      {new Date(r.createdAt).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                      {isEnd && <span style={{ opacity: 0.7, marginLeft: 6 }}>— koniec zmiany</span>}
                      {isAdj && <span style={{ opacity: 0.7, marginLeft: 6 }}>— odjęcie</span>}
                      {isNote && <span style={{ opacity: 0.7, marginLeft: 6 }}>— notatka</span>}
                    </div>
                    {r.note && (
                      <div style={{
                        opacity: 0.8, fontSize: 12, marginTop: 2,
                        fontStyle: 'italic', wordBreak: 'break-word',
                      }}>
                        {r.note}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal: Odejmij gości */}
      {showSubtract && (
        <SubtractModal
          onClose={() => setShowSubtract(false)}
          onSubmit={(count, reason) => { submitAdjustment(count, reason); setShowSubtract(false); }}
          busy={busy}
        />
      )}

      {/* Modal: Notatka */}
      {showNote && (
        <NoteModal
          onClose={() => setShowNote(false)}
          onSubmit={(text) => { submitNote(text); setShowNote(false); }}
          busy={busy}
        />
      )}
    </>
  );
};

// =====================================================================
// Modal: Odjęcie gości z powodem
// =====================================================================
const SubtractModal = ({ onClose, onSubmit, busy }) => {
  const [count, setCount] = useState('');
  const [reason, setReason] = useState('');
  const valid = parseInt(count, 10) >= 1 && reason.trim().length >= 3;
  return (
    <ModalOverlay onClose={onClose} testId="hostess-subtract-modal" title="➖ Odejmij gości" accent="#fb923c">
      <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 12 }}>
        Podaj liczbę gości do odjęcia i koniecznie krótki powód (np. „grupa wyszła bez zamówienia", „rezerwacja nie dotarła").
      </div>
      <input
        type="number" min="1" max="999"
        value={count}
        onChange={(e) => setCount(e.target.value)}
        placeholder="Ile gości odjąć"
        data-testid="hostess-subtract-count"
        inputMode="numeric"
        autoFocus
        style={modalInputStyle}
      />
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Powód odjęcia (wymagany, min. 3 znaki)"
        rows={3}
        maxLength={500}
        data-testid="hostess-subtract-reason"
        style={{ ...modalInputStyle, resize: 'vertical', fontFamily: 'inherit', minHeight: 80 }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button onClick={onClose} style={modalCancelStyle}>Anuluj</button>
        <button
          onClick={() => valid && onSubmit(parseInt(count, 10), reason)}
          disabled={!valid || busy}
          data-testid="hostess-subtract-submit"
          style={{
            ...modalSubmitStyle,
            background: valid ? '#fb923c' : 'rgba(255,255,255,0.15)',
            color: valid ? '#fff' : 'rgba(255,255,255,0.5)',
            cursor: valid && !busy ? 'pointer' : 'not-allowed',
          }}
        >
          ➖ Odejmij
        </button>
      </div>
    </ModalOverlay>
  );
};

// =====================================================================
// Modal: Notatka dla managera
// =====================================================================
const NoteModal = ({ onClose, onSubmit, busy }) => {
  const [text, setText] = useState('');
  const valid = text.trim().length >= 3;
  return (
    <ModalOverlay onClose={onClose} testId="hostess-note-modal" title="📝 Notatka dla managera" accent="#3b82f6">
      <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 12 }}>
        Wyślij krótką notatkę z dzisiejszej zmiany — manager zobaczy ją w panelu „Sprawy restauracji → Dane sprawdzające".
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={'np. „Po godz. 18 zaczął się duży ruch", „Klient zgłaszał problem z toaletą"…'}
        rows={5}
        maxLength={500}
        autoFocus
        data-testid="hostess-note-text"
        style={{ ...modalInputStyle, resize: 'vertical', fontFamily: 'inherit', minHeight: 110 }}
      />
      <div style={{ fontSize: 11, opacity: 0.55, textAlign: 'right', marginTop: -6, marginBottom: 8 }}>
        {text.length}/500
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onClose} style={modalCancelStyle}>Anuluj</button>
        <button
          onClick={() => valid && onSubmit(text)}
          disabled={!valid || busy}
          data-testid="hostess-note-submit"
          style={{
            ...modalSubmitStyle,
            background: valid ? '#3b82f6' : 'rgba(255,255,255,0.15)',
            color: valid ? '#fff' : 'rgba(255,255,255,0.5)',
            cursor: valid && !busy ? 'pointer' : 'not-allowed',
          }}
        >
          📤 Wyślij notatkę
        </button>
      </div>
    </ModalOverlay>
  );
};

// Helper modal overlay
const ModalOverlay = ({ children, onClose, title, accent, testId }) => (
  <div
    onClick={onClose}
    data-testid={testId}
    style={{
      position: 'fixed', inset: 0, zIndex: 9998,
      background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      padding: '20px 14px',
      animation: 'hostessOverlayIn 0.2s ease',
    }}
  >
    <style>{`
      @keyframes hostessOverlayIn { from { opacity: 0 } to { opacity: 1 } }
      @keyframes hostessSheetIn {
        from { transform: translateY(40px); opacity: 0; }
        to   { transform: translateY(0);    opacity: 1; }
      }
    `}</style>
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: '#101218', color: '#fff',
        borderTop: `3px solid ${accent}`,
        borderRadius: 18, padding: '18px 18px 16px',
        maxWidth: 460, width: '100%',
        boxShadow: '0 -20px 60px rgba(0,0,0,0.65)',
        animation: 'hostessSheetIn 0.25s ease',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
      }}>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, flex: 1 }}>{title}</h3>
        <button
          onClick={onClose}
          style={{
            background: 'transparent', border: 'none', color: '#fff',
            fontSize: 20, opacity: 0.6, cursor: 'pointer', padding: 4,
          }}
        >✕</button>
      </div>
      {children}
    </div>
  </div>
);

const modalInputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 14px',
  fontSize: 15,
  fontWeight: 600,
  borderRadius: 12,
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'rgba(255,255,255,0.92)',
  color: '#1a1a1a',
  outline: 'none',
  marginBottom: 10,
};

const modalCancelStyle = {
  flex: 1,
  padding: '12px 14px',
  fontSize: 14, fontWeight: 700,
  borderRadius: 12,
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'transparent',
  color: 'rgba(255,255,255,0.75)',
  cursor: 'pointer',
};

const modalSubmitStyle = {
  flex: 1.4,
  padding: '12px 14px',
  fontSize: 14, fontWeight: 900,
  borderRadius: 12,
  border: 'none',
};

const pageStyle = (accent, text) => ({
  minHeight: '100vh',
  background: `linear-gradient(160deg, ${accent}33 0%, #0f1117 50%, #050608 100%)`,
  color: text,
  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
});

export default HostessGuestCounterPage;
